import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, addDays, subDays, addWeeks, subWeeks, addMonths, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { SelectButton } from 'primereact/selectbutton';
import { Menu } from 'primereact/menu';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import { TooltipOptions } from 'primereact/tooltip/tooltipoptions';
import {
  AgendaAppointment,
  AgendaProfessional,
  AgendaSchedule,
  AgendaStats,
  EAgendaTab,
  EAgendaLens,
  AgendaViewMode,
  WeekStats,
  WeekBlock,
  MonthDayCell,
  MultiDayAppointmentRow,
  MonthlyCalendarResponse,
} from './types/agenda.types';
import { Establishment } from '../../interface/interface';
import {
  getAgendaAppointments,
  getAgendaSummary,
  getAgendaOptions,
  getAgendaProfessionalsCombo,
  getCoverageLimited,
  registerArrival,
  callPatient,
  revertAppointmentStatus,
  printAppointmentsPdf,
  exportAppointmentsExcel,
  getAgendaAppointmentsRange,
  getAgendaSummaryRange,
  getMonthlyCalendarSummary,
} from './services/AgendaService';

import ListView from './components/ListView';
import ScheduleView from './components/ScheduleView';
import NewAppointmentModal from './components/NewAppointmentModal';
import PatientCheckInModal from './components/PatientCheckInModal';
import CancelAppointmentModal from './components/CancelAppointmentModal';
import BillingScreen, { BillingStart } from './components/billing/BillingScreen';
import useNotify from '../../hooks/useNotify';
import MedicalEvolutionModal from './components/MedicalEvolutionModal';
import AppointmentDetailDrawer from './components/AppointmentDetailDrawer';
import ChangeLocationModal from './components/ChangeLocationModal';
import AppointmentLogModal from './components/AppointmentLogModal';
import AssignPriorityModal from './components/AssignPriorityModal';
import OutOfScheduleScreen from './components/OutOfScheduleScreen';
import WeekListView from './components/WeekListView';
import WeekScheduleView from './components/WeekScheduleView';
import { appointmentsToWeekBlocks } from './utils/weekBlockUtils';
import MonthListView from './components/MonthListView';
import MonthCalendarView from './components/MonthCalendarView';
import LensKPIs from './components/LensKPIs';
import PatientClinicalHistoryView from '../../components/PatientClinicalHistory/PatientClinicalHistoryModal';
import { useAgendaMqtt } from './hooks/useAgendaMqtt';
import { EAppointmentStateValues, EAppointmentState } from '../../enums/EAppointmentState';
import { parseApiError } from './types/error.types';
import './AgendaView.scss';
import { loadAllEstablishments } from '../../servicesHooks/Location/useLocationConfigService';
import NvTooltips from '../../components/novarium/NvTooltips';

const DAY_OF_WEEK_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

function formatHeaderDate(date: Date): string {
  const dayName = DAY_OF_WEEK_NAMES[date.getDay()];
  const day = date.getDate();
  const month = format(date, 'MMM', { locale: es });
  return `${dayName} ${day} ${month.charAt(0).toUpperCase() + month.slice(1)}`;
}

function formatDateLabel(date: Date, lens: EAgendaLens): string {
  if (lens === EAgendaLens.DAY) return formatHeaderDate(date);
  if (lens === EAgendaLens.WEEK) {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
    const startDay = weekStart.getDate();
    const endDay = weekEnd.getDate();
    const month = format(weekEnd, 'MMM', { locale: es });
    return `${startDay} – ${endDay} ${month.charAt(0).toUpperCase() + month.slice(1)}`;
  }
  const monthYear = format(date, 'MMMM yyyy', { locale: es });
  return monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
}

const LENS_OPTIONS = [
  { label: 'Día', value: EAgendaLens.DAY },
  { label: 'Semana', value: EAgendaLens.WEEK },
  { label: 'Mes', value: EAgendaLens.MONTH },
];

const NAV_LABELS: Record<EAgendaLens, { prev: string; next: string }> = {
  [EAgendaLens.DAY]: { prev: 'Día anterior', next: 'Día siguiente' },
  [EAgendaLens.WEEK]: { prev: 'Semana anterior', next: 'Semana siguiente' },
  [EAgendaLens.MONTH]: { prev: 'Mes anterior', next: 'Mes siguiente' },
};

const TOOLTIP_OPTIONS: TooltipOptions = { className: 'nv-tooltip', event: 'both', position: 'top' };

/** Las agendas no tienen id propio: se identifican por el tipo y sus plantillas. */
function getAgendaKey(agenda: AgendaSchedule): string {
  return `${agenda.agendaType}:${agenda.templateIds.join(',')}`;
}

function getAgendaDisplayName(agenda: AgendaSchedule): string {
  if (agenda.agendaType === 'service') {
    return agenda.name || agenda.speciality || 'Agenda de servicio';
  }
  return [agenda.professionalName, agenda.speciality, agenda.name]
    .filter(Boolean)
    .join(' — ') || 'Agenda profesional';
}

export default function AgendaView() {
  const [agendaOptions, setAgendaOptions] = useState<AgendaSchedule[]>([]);
  const [selectedAgendas, setSelectedAgendas] = useState<AgendaSchedule[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [activeTab, setActiveTab] = useState<EAgendaTab>(EAgendaTab.LIST);
  const [lens, setLens] = useState<EAgendaLens>(EAgendaLens.DAY);
  const [appointments, setAppointments] = useState<AgendaAppointment[]>([]);
  const [stats, setStats] = useState<AgendaStats | null>(null);
  const [searchText, setSearchText] = useState('');
  const DEFAULT_STATE_FILTERS = [
    EAppointmentStateValues[EAppointmentState.SCHEDULLED],
    EAppointmentStateValues[EAppointmentState.CONFIRMED],
    EAppointmentStateValues[EAppointmentState.INQUEUE],
    EAppointmentStateValues[EAppointmentState.PENDING_ATTENTION],
    EAppointmentStateValues[EAppointmentState.INATTENTION],
    EAppointmentStateValues[EAppointmentState.IN_OBSERVATION],
    EAppointmentStateValues[EAppointmentState.COMPLETED],
  ];
  const [stateFilter, setStateFilter] = useState<number[]>(DEFAULT_STATE_FILTERS);
  const [coverageFilter, setCoverageFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreAppointments, setHasMoreAppointments] = useState(false);
  const PAGE_SIZE = 50;
  const [activeScreen, setActiveScreen] = useState<'agenda' | 'newturno' | 'newseries' | 'outofschedule' | 'billing'>('agenda');
  const [availabilityBooking, setAvailabilityBooking] = useState<{ time: string; agenda: AgendaSchedule } | null>(null);
  const [availabilityRefreshKey, setAvailabilityRefreshKey] = useState(0);
  const [reschedulePatientId, setReschedulePatientId] = useState<number | null>(null);
  const notify = useNotify();
  const [billingStart, setBillingStart] = useState<BillingStart | null>(null);
  /** "Registrar y cobrar" en la llegada: si no se registra la orden, se revierte la llegada (como Vaadin). */
  const [revertArrivalOf, setRevertArrivalOf] = useState<number | null>(null);
  const [checkInAppointmentId, setCheckInAppointmentId] = useState<number | null>(null);
  const [editPatientAppointmentId, setEditPatientAppointmentId] = useState<number | null>(null);
  const [cancelTarget, setCancelTarget] = useState<{
    id: number;
    patientName: string;
    patientId?: number | null;
    professionalId?: number | null;
    mode: 'cancel' | 'reschedule';
  } | null>(null);
  const [evolutionAppointment, setEvolutionAppointment] = useState<AgendaAppointment | null>(null);
  const [detailAppointment, setDetailAppointment] = useState<AgendaAppointment | null>(null);
  const [professionalFilter, setProfessionalFilter] = useState<string | null>(null);
  const [establishmentOptions, setEstablishmentOptions] = useState<Establishment[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [showChangeLocation, setShowChangeLocation] = useState(false);
  /** Span que envuelve a «Cambio de consultorio»: lleva el tooltip con el motivo cuando está deshabilitado. */
  const [changeLocationTipEl, setChangeLocationTipEl] = useState<HTMLSpanElement | null>(null);
  const [clinicalHistoryPatientId, setClinicalHistoryPatientId] = useState<number | null>(null);
  const [logTarget, setLogTarget] = useState<{ id: number; patientName: string } | null>(null);
  const [priorityTarget, setPriorityTarget] = useState<{ id: number; patientName: string; currentPriorityId: number | null } | null>(null);
  const [coverageLimited, setCoverageLimited] = useState(false);
  const [professionalOptions, setProfessionalOptions] = useState<AgendaProfessional[]>([]);
  const [selectedProfessional, setSelectedProfessional] = useState<AgendaProfessional | null>(null);

  const selectedAgendaType: AgendaViewMode | null = selectedAgendas[0]?.agendaType ?? null;
  const viewMode: AgendaViewMode = selectedAgendaType ?? 'professional';

  useEffect(() => {
    getCoverageLimited().then(({ limited }) => setCoverageLimited(limited)).catch(() => setCoverageLimited(false));
  }, []);

  useEffect(() => {
    let active = true;
    loadAllEstablishments().then((establishments) => {
      if (!active) return;
      const activeEstablishments = (establishments ?? []).filter((establishment) => establishment.active);
      setEstablishmentOptions(activeEstablishments);
      setSelectedBuildingId((current) => {
        if (current && activeEstablishments.some((establishment) => establishment.buildingid === current)) return current;
        return activeEstablishments.length === 1 ? activeEstablishments[0].buildingid : null;
      });
    }).catch(() => {
      if (active) setEstablishmentOptions([]);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    getAgendaProfessionalsCombo({ filterValue: '' })
      .then(setProfessionalOptions)
      .catch(() => setProfessionalOptions([]));
  }, []);

  const dateParam = useMemo(() => format(selectedDate, 'yyyy-MM-dd'), [selectedDate]);
  const selectedTemplateIds = useMemo(
    () => Array.from(new Set(selectedAgendas.flatMap(agenda => agenda.templateIds))),
    [selectedAgendas],
  );
  const professionalIds = useMemo(
    () => Array.from(new Set(selectedAgendas.filter(agenda => agenda.agendaType === 'professional' && agenda.professionalId > 0).map(agenda => agenda.professionalId))),
    [selectedAgendas],
  );
  const specialityIds = useMemo(
    () => Array.from(new Set(selectedAgendas.flatMap(agenda => agenda.specialityIds ?? []))),
    [selectedAgendas],
  );
  const selectedTemplateIdsKey = selectedTemplateIds.join(',');
  const entitySelected = selectedAgendas.length > 0 || !!selectedProfessional;
  const bookingSchedule = availabilityBooking?.agenda ?? (selectedAgendas.length === 1 ? selectedAgendas[0] : null);
  const initialProfessional = useMemo(() => {
    const agenda = bookingSchedule?.agendaType === 'professional' && bookingSchedule.professionalId > 0
      ? bookingSchedule
      : null;
    return agenda ? { id: agenda.professionalId, name: agenda.professionalName } : null;
  }, [bookingSchedule]);
  useEffect(() => {
    if (!selectedBuildingId) {
      setAgendaOptions([]);
      setSelectedAgendas([]);
      return () => undefined;
    }
    let active = true;
    getAgendaOptions(undefined, dateParam, selectedBuildingId).then((options) => {
      if (!active) return;
      setAgendaOptions(options);
      setSelectedAgendas((current) => current
        .map((selected) => options.find((option) =>
          option.agendaType === selected.agendaType &&
          option.templateIds.join(',') === selected.templateIds.join(',')))
        .filter((agenda): agenda is AgendaSchedule => !!agenda));
    }).catch(() => {
      if (active) setAgendaOptions([]);
    });
    return () => { active = false; };
  }, [dateParam, selectedBuildingId]);

  // Con un profesional elegido, el combo muestra solo sus agendas vigentes en la fecha y el establecimiento.
  const agendaOptionsWithLabels = useMemo(
    () => agendaOptions
      .filter(agenda => !selectedProfessional ||
        (agenda.agendaType === 'professional' && agenda.professionalId === selectedProfessional.id))
      .map(agenda => ({
        ...agenda,
        key: getAgendaKey(agenda),
        displayName: getAgendaDisplayName(agenda),
      })),
    [agendaOptions, selectedProfessional],
  );
  const groupedAgendaOptions = useMemo(() => [
    {
      label: 'Servicios',
      items: agendaOptionsWithLabels.filter(agenda => agenda.agendaType === 'service'),
    },
    {
      label: 'Profesionales',
      items: agendaOptionsWithLabels.filter(agenda => agenda.agendaType === 'professional'),
    },
  ].filter(group => group.items.length > 0), [agendaOptionsWithLabels]);

  const handleAgendaSelectionChange = useCallback((value: AgendaSchedule[]) => {
    const currentKeys = new Set(selectedAgendas.map(getAgendaKey));
    const newlySelected = value.find(agenda => !currentKeys.has(getAgendaKey(agenda)));
    if (newlySelected && selectedAgendaType && newlySelected.agendaType !== selectedAgendaType) {
      setSelectedAgendas([newlySelected]);
      return;
    }
    setSelectedAgendas(value.filter(agenda => !selectedAgendaType || agenda.agendaType === selectedAgendaType));
  }, [selectedAgendas, selectedAgendaType]);

  // El MultiSelect trabaja con la clave de cada agenda: así reconoce las elegidas aunque las opciones se recarguen
  // (al cambiar de fecha) y puede mostrar sus chips.
  const selectedAgendaKeys = useMemo(() => selectedAgendas.map(getAgendaKey), [selectedAgendas]);
  const handleAgendaKeysChange = useCallback((keys: string[]) => {
    handleAgendaSelectionChange(keys
      .map(key => agendaOptionsWithLabels.find(agenda => agenda.key === key))
      .filter((agenda): agenda is AgendaSchedule & { key: string; displayName: string } => !!agenda));
  }, [agendaOptionsWithLabels, handleAgendaSelectionChange]);

  // When a professional is selected but no specific agenda, query by professionalIds only
  // (no scheduleTemplateIds) so out-of-agenda appointments are included.
  const useProfessionalFilter = !!selectedProfessional && selectedAgendas.length === 0;
  const queryProfessionalIds = useProfessionalFilter ? [selectedProfessional!.id] : null;
  const queryTemplateIds = useProfessionalFilter ? null : (selectedTemplateIds.length > 0 ? selectedTemplateIds : null);

  // Load appointments and stats for the exact selected agenda templates.
  const loadData = useCallback(() => {
    if (!entitySelected) {
      setAppointments([]);
      setStats(null);
      setHasMoreAppointments(false);
      return;
    }
    setLoading(true);
    Promise.all([
      getAgendaAppointments(dateParam, queryProfessionalIds, null, queryTemplateIds, 0, PAGE_SIZE, stateFilter, coverageFilter, null, null, selectedBuildingId),
      getAgendaSummary(dateParam, queryProfessionalIds, null, queryTemplateIds, selectedBuildingId),
    ])
      .then(([paginatedAppts, summary]) => {
        setAppointments(paginatedAppts.data);
        setStats(summary);
        setHasMoreAppointments(paginatedAppts.data.length < paginatedAppts.totalCount);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [dateParam, selectedTemplateIdsKey, entitySelected, stateFilter, coverageFilter, selectedBuildingId, selectedProfessional]);

  const loadMoreAppointments = useCallback(() => {
    if (!entitySelected || loadingMore || !hasMoreAppointments) return;
    setLoadingMore(true);
    const offset = appointments.length;
    getAgendaAppointments(dateParam, queryProfessionalIds, null, queryTemplateIds, offset, PAGE_SIZE, stateFilter, coverageFilter, null, null, selectedBuildingId)
      .then((paginatedAppts) => {
        setAppointments((prev) => [...prev, ...paginatedAppts.data]);
        setHasMoreAppointments(offset + paginatedAppts.data.length < paginatedAppts.totalCount);
      })
      .catch(console.error)
      .finally(() => setLoadingMore(false));
  }, [entitySelected, loadingMore, hasMoreAppointments, appointments.length, dateParam, queryProfessionalIds, queryTemplateIds, stateFilter, coverageFilter, selectedBuildingId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAvailabilitySlotSelect = useCallback((time: string, agenda: AgendaSchedule) => {
    setAvailabilityBooking({ time, agenda });
    setActiveScreen('newturno');
  }, []);

  const handleNewAppointmentClose = useCallback(() => {
    setActiveScreen('agenda');
    setReschedulePatientId(null);
    setAvailabilityBooking(null);
  }, []);

  const handleAppointmentBooked = useCallback(() => {
    setAvailabilityRefreshKey((current) => current + 1);
    loadData();
  }, [loadData]);

  // Subscribe to live agenda updates for the selected professional or specialities.
  useAgendaMqtt(
    viewMode === 'professional' && professionalIds.length === 1 ? professionalIds[0] : null,
    specialityIds,
    loadData,
  );

  // Navigation handlers (lens-aware)
  const goToPrev = () => {
    if (lens === EAgendaLens.DAY) setSelectedDate((d) => subDays(d, 1));
    else if (lens === EAgendaLens.WEEK) setSelectedDate((d) => subWeeks(d, 1));
    else setSelectedDate((d) => subMonths(d, 1));
  };
  const goToNext = () => {
    if (lens === EAgendaLens.DAY) setSelectedDate((d) => addDays(d, 1));
    else if (lens === EAgendaLens.WEEK) setSelectedDate((d) => addWeeks(d, 1));
    else setSelectedDate((d) => addMonths(d, 1));
  };

  // Download handlers (moved from header buttons to ListView download menu)
  const handleDownloadPdf = async () => {
    try {
      const blob = await printAppointmentsPdf({
        date: dateParam,
        professionalId: viewMode === 'professional' && professionalIds.length > 0 ? professionalIds[0] : null,
        specialityId: viewMode === 'service' && specialityIds.length > 0 ? specialityIds[0] : null,
        stateFilter, coverageFilter,
        locationFilter: null, appointmentTypeFilter: null, buildingId: selectedBuildingId,
      });
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(url);
    } catch (err) {
      notify.error(parseApiError(err, 'No se pudo generar el PDF. Intentá de nuevo.'));
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const blob = await exportAppointmentsExcel({
        date: dateParam,
        professionalId: viewMode === 'professional' && professionalIds.length > 0 ? professionalIds[0] : null,
        specialityId: viewMode === 'service' && specialityIds.length > 0 ? specialityIds[0] : null,
        stateFilter, coverageFilter,
        locationFilter: null, appointmentTypeFilter: null, buildingId: selectedBuildingId,
      });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Turnos.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      notify.error(parseApiError(err, 'No se pudo exportar a Excel. Intentá de nuevo.'));
    }
  };

  const downloadMenuRef = useRef<Menu>(null);
  const downloadMenuItems = [
    { label: 'Imprimir PDF', icon: 'pi pi-file-pdf', command: () => { void handleDownloadPdf(); } },
    { label: 'Exportar Excel', icon: 'pi pi-file-excel', command: () => { void handleDownloadExcel(); } },
  ];

  // Action handlers
  const handleCancelAppointment = useCallback(
    (appointmentId: number, patientName: string, patientId?: number | null, professionalMedereEntity?: number | null) => {
      setCancelTarget({ id: appointmentId, patientName, patientId, professionalId: professionalMedereEntity, mode: 'cancel' });
    },
    []
  );

  const handleRescheduleAppointment = useCallback(
    (appointmentId: number, patientName: string, patientId?: number | null, professionalMedereEntity?: number | null) => {
      setCancelTarget({ id: appointmentId, patientName, patientId, professionalId: professionalMedereEntity, mode: 'reschedule' });
    },
    []
  );

  const handleRegisterArrival = useCallback(
    (appointmentId: number) => {
      setCheckInAppointmentId(appointmentId);
    },
    []
  );

  const handleCallPatient = useCallback(
    async (appointmentId: number, appointment?: AgendaAppointment) => {
      try {
        await callPatient(appointmentId);
        loadData();
        if (appointment) {
          setEvolutionAppointment(appointment);
        }
      } catch (err) {
        notify.error(parseApiError(err, 'No se pudo llamar al paciente. Intentá de nuevo.'));
      }
    },
    [loadData, notify]
  );

  const handleFinishAttention = useCallback(
    (appointmentId: number, appointment?: AgendaAppointment) => {
      if (appointment) {
        setEvolutionAppointment(appointment);
      }
    },
    []
  );

  const handleRevertStatus = useCallback(
    (appointmentId: number) => {
      const patientName = appointments.find((a) => a.patientcarequeue === appointmentId)?.patientname;
      confirmDialog({
        header: '¿Revertir el estado del turno?',
        message: patientName
          ? `El turno de ${patientName} vuelve al estado anterior.`
          : 'El turno vuelve al estado anterior.',
        className: 'nv-scope nv-confirm',
        // PrimeReact 9 no tiene defaultFocus: el foco en la opción segura (Cancelar) va con autoFocus
        footer: ({ accept, reject }) => (
          <>
            <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={reject} autoFocus />
            <Button type="button" className="buttonMain" label="Revertir" onClick={accept} />
          </>
        ),
        accept: async () => {
          try {
            await revertAppointmentStatus(appointmentId);
            loadData();
          } catch (err) {
            notify.error(parseApiError(err, 'No se pudo revertir el estado del turno. Intentá de nuevo.'));
          }
        },
      });
    },
    [appointments, loadData, notify]
  );

  // Filtered appointments based on search text and professional filter
  // Note: stateFilter is sent to backend (server-side filtering)
  const filteredAppointments = useMemo(() => {
    let result = appointments;
    if (searchText.trim()) {
      const needle = searchText.toLowerCase().trim();
      result = result.filter(
        (a) =>
          (a.patientname && a.patientname.toLowerCase().includes(needle)) ||
          (a.document && a.document.toLowerCase().includes(needle)) ||
          (a.clinichistoryid && a.clinichistoryid.toLowerCase().includes(needle))
      );
    }
    if (professionalFilter) {
      result = result.filter((a) => a.professional === professionalFilter);
    }
    return result;
  }, [appointments, searchText, professionalFilter]);

  // Keep drawer data fresh when appointments update via MQTT
  const liveDetailAppointment = useMemo(() => {
    if (!detailAppointment) return null;
    return appointments.find((a) => a.patientcarequeue === detailAppointment.patientcarequeue) || detailAppointment;
  }, [appointments, detailAppointment]);

  const handleOpenDetail = useCallback((appointment: AgendaAppointment) => {
    setDetailAppointment(appointment);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailAppointment(null);
  }, []);

  /** "Paciente · Obra social Plan · 09:40 jue 9 jul" para el cobro del turno. */
  const billingSubtitle = useCallback((appointment: AgendaAppointment | undefined) => {
    if (!appointment) return undefined;
    const coverage = [appointment.healthentityname, appointment.planname].filter(Boolean).join(' ');
    const when = appointment.appointmentdatetime ? new Date(appointment.appointmentdatetime) : null;
    const time = when
      ? `${format(when, 'HH:mm')} ${format(when, 'EEE d MMM', { locale: es }).replace(/\./g, '')}`
      : null;
    return [coverage, time].filter(Boolean).join(' · ') || undefined;
  }, []);

  /** Cobro del turno: arranca registrando la orden. */
  const startOrderRegistration = useCallback((appointmentId: number, revertArrivalOnCancel = false) => {
    const appointment = appointments.find((a) => a.patientcarequeue === appointmentId);
    if (!appointment?.medereentity) return;
    setBillingStart({
      kind: 'order',
      appointmentId,
      patientId: appointment.medereentity,
      patientName: appointment.patientname ?? '',
      subtitle: billingSubtitle(appointment),
    });
    setRevertArrivalOf(revertArrivalOnCancel ? appointmentId : null);
    setActiveScreen('billing');
  }, [appointments, billingSubtitle]);

  const startBilling = useCallback((start: BillingStart) => {
    setBillingStart(start);
    setRevertArrivalOf(null);
    setActiveScreen('billing');
  }, []);

  const handleBillingFinished = useCallback(async ({ orderRegistered }: { orderRegistered: boolean }) => {
    setActiveScreen('agenda');
    setBillingStart(null);
    if (revertArrivalOf != null && !orderRegistered) {
      try {
        await revertAppointmentStatus(revertArrivalOf);
        notify.success('Se canceló el registro de llegada');
      } catch (err) {
        notify.error(parseApiError(err, 'No se pudo cancelar el registro de llegada. Intentá de nuevo.'));
      }
    }
    setRevertArrivalOf(null);
    loadData();
  }, [revertArrivalOf, notify, loadData]);

  // Stats counts
  const waitingCount = stats?.waiting ?? 0;
  const inAttentionCount = stats?.inAttention ?? 0;
  const completedCount = stats?.completed ?? 0;
  const missedCount = stats?.missed ?? 0;
  const totalAppointments = stats?.totalAppointments ?? 0;
  const totalSlots = stats?.totalSlots ?? 0;
  const overturnCount = useMemo(() => appointments.filter((a) => a.outofschedule).length, [appointments]);

  // ─── Week/Month data (fetched from backend) ──────────
  const [weekStats, setWeekStats] = useState<WeekStats>({ totalAppointments: 0, occupied: 0, free: 0, overturnCount: 0 });
  const [weekListRows, setWeekListRows] = useState<MultiDayAppointmentRow[]>([]);
  const [weekBlocks, setWeekBlocks] = useState<WeekBlock[]>([]);
  const [weekLoading, setWeekLoading] = useState(false);
  const [monthCalendar, setMonthCalendar] = useState<MonthlyCalendarResponse | null>(null);
  const [monthListRows, setMonthListRows] = useState<MultiDayAppointmentRow[]>([]);
  const [monthLoading, setMonthLoading] = useState(false);

  const DAY_NAMES_SHORT = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

  // Build week day headers from selectedDate
  const weekDayHeaders = useMemo(() => {
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    return Array.from({ length: 6 }, (_, i) => {
      const d = addDays(weekStart, i);
      return {
        dayName: DAY_NAMES_SHORT[d.getDay()],
        dayNumber: d.getDate(),
        isToday: format(d, 'yyyy-MM-dd') === todayStr,
      };
    });
  }, [selectedDate]);

  // Fetch week data when lens is WEEK and entity is selected
  useEffect(() => {
    if (lens !== EAgendaLens.WEEK || !entitySelected) {
      setWeekListRows([]);
      setWeekBlocks([]);
      setWeekStats({ totalAppointments: 0, occupied: 0, free: 0, overturnCount: 0 });
      return;
    }
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
    const from = format(weekStart, 'yyyy-MM-dd');
    const to = format(weekEnd, 'yyyy-MM-dd');

    setWeekLoading(true);
    Promise.all([
      getAgendaAppointmentsRange(from, to, queryProfessionalIds, null, queryTemplateIds, 0, 500, null, null, selectedBuildingId),
      getAgendaSummaryRange(from, to, queryProfessionalIds, null, queryTemplateIds, selectedBuildingId),
    ]).then(([appts, summary]) => {
      // Transform appointments to list rows
      const rows: MultiDayAppointmentRow[] = appts.data.map((a) => {
        const apptDate = new Date(a.appointmentdatetime);
        return {
          dayLabel: `${DAY_NAMES_SHORT[apptDate.getDay()]} ${apptDate.getDate()}`,
          date: format(apptDate, 'dd/MM'),
          time: a.appointmentfromtime?.substring(0, 5) ?? '',
          patientName: a.patientname ?? '',
          statusLabel: a.appointmentstate ?? '',
          stateId: a.appointmentstateid,
          professional: a.professional ?? '',
          appointmentId: a.patientcarequeue,
        };
      });
      setWeekListRows(rows);
      setWeekBlocks(appointmentsToWeekBlocks(appts.data, weekStart));
      setWeekStats({
        totalAppointments: summary.totalAppointments,
        occupied: summary.totalAppointments,
        free: (summary.totalSlots ?? 0) - summary.totalAppointments,
        overturnCount: summary.overturnCount ?? 0,
      });
    }).catch(console.error).finally(() => setWeekLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lens, selectedDate, entitySelected, selectedTemplateIdsKey, selectedBuildingId, selectedProfessional]);

  // Fetch month data when lens is MONTH and entity is selected
  useEffect(() => {
    if (lens !== EAgendaLens.MONTH || !entitySelected) {
      setMonthCalendar(null);
      setMonthListRows([]);
      return;
    }
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth() + 1;
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    const from = format(monthStart, 'yyyy-MM-dd');
    const to = format(monthEnd, 'yyyy-MM-dd');

    setMonthLoading(true);
    Promise.all([
      getAgendaAppointmentsRange(from, to, queryProfessionalIds, null, queryTemplateIds, 0, 1000, null, null, selectedBuildingId),
      getMonthlyCalendarSummary(year, month, queryProfessionalIds, null, queryTemplateIds, selectedBuildingId),
    ]).then(([appts, calendar]) => {
      const rows: MultiDayAppointmentRow[] = appts.data.map((a) => {
        const apptDate = new Date(a.appointmentdatetime);
        return {
          dayLabel: `${DAY_NAMES_SHORT[apptDate.getDay()]} ${apptDate.getDate()}`,
          date: format(apptDate, 'dd/MM'),
          time: a.appointmentfromtime?.substring(0, 5) ?? '',
          patientName: a.patientname ?? '',
          statusLabel: a.appointmentstate ?? '',
          stateId: a.appointmentstateid,
          professional: a.professional ?? '',
          appointmentId: a.patientcarequeue,
        };
      });
      setMonthListRows(rows);
      setMonthCalendar(calendar);
    }).catch(console.error).finally(() => setMonthLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lens, selectedDate, entitySelected, selectedTemplateIdsKey, selectedBuildingId, selectedProfessional]);

  // Build month cells from MonthlyCalendarResponse
  const monthCells = useMemo((): MonthDayCell[] => {
    if (!monthCalendar) return [];
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const todayStr = format(new Date(), 'yyyy-MM-dd');

    // Build a lookup from the API response
    const dayMap = new Map<string, { occupied: number; free: number; isExclusion: boolean }>();
    for (const d of monthCalendar.days) {
      dayMap.set(d.date, { occupied: d.occupied, free: d.free, isExclusion: d.isExclusion });
    }

    const cells: MonthDayCell[] = [];

    // Padding days before the 1st (Monday = 0 offset with weekStartsOn:1)
    let firstDayOfWeek = getDay(firstDay); // 0=Sun..6=Sat
    const padBefore = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // convert to Mon=0
    for (let i = padBefore; i > 0; i--) {
      const d = addDays(firstDay, -i);
      cells.push({ date: d, dayNumber: d.getDate(), isCurrentMonth: false, isToday: false, isExclusion: false, occupied: 0, free: 0 });
    }

    // Days of the month
    const lastDay = endOfMonth(firstDay);
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const d = new Date(year, month, day);
      const dateStr = format(d, 'yyyy-MM-dd');
      const info = dayMap.get(dateStr);
      cells.push({
        date: d,
        dayNumber: day,
        isCurrentMonth: true,
        isToday: dateStr === todayStr,
        isExclusion: info?.isExclusion ?? false,
        occupied: info?.occupied ?? 0,
        free: info?.free ?? 0,
      });
    }

    // Padding days after to complete the grid (multiple of 7)
    while (cells.length % 7 !== 0) {
      const d = addDays(lastDay, cells.length - padBefore - lastDay.getDate() + 1);
      cells.push({ date: d, dayNumber: d.getDate(), isCurrentMonth: false, isToday: false, isExclusion: false, occupied: 0, free: 0 });
    }

    return cells;
  }, [monthCalendar, selectedDate]);

  // Month stats from calendar totals
  const monthStats = useMemo(() => monthCalendar?.totals ?? { scheduled: 0, free: 0, exclusionDays: 0, totalSlots: 0 }, [monthCalendar]);

  const tabOptions = [
    { label: 'Lista', value: EAgendaTab.LIST },
    { label: 'Agenda', value: EAgendaTab.SCHEDULE },
  ];

  const navLabels = NAV_LABELS[lens];
  const changeLocationDisabledReason = viewMode !== 'professional' || professionalIds.length === 0
    ? 'Elegí una agenda de profesional primero'
    : null;
  const appointmentsCount = filteredAppointments.length;

  // Cobro del turno (pantalla completa, Novarium): fuera de .agenda-view, con su propia raíz nv-scope
  if (activeScreen === 'billing' && billingStart) {
    return (
      <>
        <ConfirmDialog />
        <NvTooltips />
        <BillingScreen start={billingStart} onFinished={handleBillingFinished} />
      </>
    );
  }

  return (
    <>
      <ConfirmDialog />
      {/* Un solo Tooltip para los elementos nv-tip de toda la Agenda (vistas, pantallas y cobro) */}
      <NvTooltips />
      {activeScreen === 'agenda' ? (
        <div className="nv-scope nv-page agenda-view">
          <header className="nv-page-head agenda-header">
            <div className="nv-head-row">
              <h1 className="nv-h1 agenda-header__title">Agenda de turnos</h1>
              <div className="nv-row-8 agenda-header__actions">
                {/* El motivo de deshabilitado va en el span que envuelve al botón (nv-disabled-tip), no con
                    showOnDisabled: ese tooltip de PrimeReact mueve el botón dentro de otro span por fuera de React. */}
                <span
                  ref={setChangeLocationTipEl}
                  className="nv-disabled-tip"
                  tabIndex={changeLocationDisabledReason ? 0 : undefined}
                >
                  <Button
                    type="button"
                    className="buttonMain3"
                    icon="pi pi-building"
                    onClick={() => setShowChangeLocation(true)}
                    disabled={!!changeLocationDisabledReason}
                  >
                    <span className="agenda-header__label-desktop">Cambio de consultorio</span>
                    <span className="agenda-header__label-mobile">Consultorio</span>
                  </Button>
                </span>
                {changeLocationDisabledReason && changeLocationTipEl && (
                  <Tooltip
                    target={changeLocationTipEl}
                    content={changeLocationDisabledReason}
                    className="nv-tooltip"
                    event="both"
                    position="top"
                  />
                )}
                <Button
                  type="button"
                  className="buttonMain2"
                  outlined
                  label="Fuera de agenda"
                  onClick={() => setActiveScreen('outofschedule')}
                />
                <Button
                  type="button"
                  className="buttonMain2"
                  outlined
                  icon="pi pi-plus-circle"
                  label="Sesiones"
                  onClick={() => setActiveScreen('newseries')}
                />
                <Button
                  type="button"
                  className="buttonMain"
                  icon="pi pi-plus-circle"
                  label="Turno"
                  onClick={() => setActiveScreen('newturno')}
                />
              </div>
            </div>
            <p className="nv-muted agenda-header__subtitle">Turnos y cola del día en una sola vista.</p>
          </header>

          {/* Fecha, vista y selección de agenda */}
          <div className="agenda-nav">
            {/* TODO(design-system): el DS no define un navegador de fecha (‹ fecha ›); se arma con nv-icon + el período en texto. */}
            <div className="nv-field-inline-action agenda-nav__date-controls">
              <Button
                type="button"
                text
                className="nv-icon"
                icon="pi pi-chevron-left"
                aria-label={navLabels.prev}
                tooltip={navLabels.prev}
                tooltipOptions={TOOLTIP_OPTIONS}
                onClick={goToPrev}
              />
              <span className="nv-strong agenda-nav__date-label">
                {formatDateLabel(selectedDate, lens)}
              </span>
              <Button
                type="button"
                text
                className="nv-icon"
                icon="pi pi-chevron-right"
                aria-label={navLabels.next}
                tooltip={navLabels.next}
                tooltipOptions={TOOLTIP_OPTIONS}
                onClick={goToNext}
              />
            </div>
            <div className="nv-field-inline-action">
              <SelectButton
                value={lens}
                options={LENS_OPTIONS}
                onChange={(e) => { if (e.value != null) setLens(e.value); }}
              />
            </div>
            <div className="nv-field nv-w-m agenda-nav__filter">
            <label htmlFor="agenda-building">Establecimiento</label>
            <Dropdown
              inputId="agenda-building"
              value={selectedBuildingId}
              options={establishmentOptions}
              optionLabel="buildingname"
              optionValue="buildingid"
              onChange={(event) => {
                setSelectedBuildingId(event.value ?? null);
                setSelectedAgendas([]);
              }}
              placeholder="Seleccionar"
              showClear
              emptyMessage="No se encontraron establecimientos"
              emptyFilterMessage="No se encontraron establecimientos"
              panelClassName="nv-scope"
            />
            </div>
            <div className="nv-field nv-w-m agenda-nav__filter">
            <label htmlFor="agenda-professional">Profesional</label>
            <Dropdown
              inputId="agenda-professional"
              value={selectedProfessional}
              options={professionalOptions}
              optionLabel="name"
              onChange={(e) => {
                setSelectedProfessional(e.value ?? null);
                setSelectedAgendas([]);
              }}
              placeholder="Seleccionar"
              showClear
              filter
              filterPlaceholder="Buscar profesional"
              emptyMessage="No se encontraron profesionales"
              emptyFilterMessage="No se encontraron profesionales"
              panelClassName="nv-scope"
            />
            </div>
            <div className="nv-field nv-w-l agenda-nav__filter">
            <label htmlFor="agenda-agendas">Agenda</label>
            <MultiSelect
              inputId="agenda-agendas"
              value={selectedAgendaKeys}
              options={groupedAgendaOptions}
              optionLabel="displayName"
              optionValue="key"
              optionGroupLabel="label"
              optionGroupChildren="items"
              onChange={(e) => handleAgendaKeysChange(e.value as string[])}
              placeholder={selectedBuildingId ? 'Seleccionar' : 'Elegí un establecimiento primero'}
              disabled={!selectedBuildingId}
              filter
              filterPlaceholder="Buscar agenda"
              emptyFilterMessage="No se encontraron agendas"
              display="chip"
              panelClassName="nv-scope"
            />
            </div>
          </div>

          {entitySelected && lens === EAgendaLens.DAY && (
            <LensKPIs cards={[
              { value: waitingCount, label: 'En espera' },
              { value: inAttentionCount, label: 'En atención' },
              { value: completedCount, label: 'Atendidos' },
              { value: missedCount, label: 'Ausentes' },
              {
                value: totalSlots > 0
                  ? <>{totalAppointments}<span className="nv-muted agenda-stats__total"> / {totalSlots}</span></>
                  : totalAppointments,
                label: 'Ocupación del día',
                extra: overturnCount > 0
                  ? <Tag className="nv-tag-selected" value={`${overturnCount} ${overturnCount === 1 ? 'sobreturno' : 'sobreturnos'}`} />
                  : null,
              },
            ]} />
          )}
          {entitySelected && lens === EAgendaLens.WEEK && (
            <LensKPIs cards={[
              { value: weekStats.totalAppointments, label: 'Turnos esta semana' },
              { value: weekStats.occupied, label: 'Ocupados' },
              { value: weekStats.free || '—', label: 'Libres' },
              { value: weekStats.overturnCount, label: 'Sobreturnos' },
            ]} />
          )}
          {entitySelected && lens === EAgendaLens.MONTH && (
            <LensKPIs cards={[
              { value: monthStats.scheduled, label: 'Agendados' },
              { value: monthStats.free, label: 'Libres' },
              { value: monthStats.exclusionDays, label: 'Días con exclusión' },
              { value: monthStats.totalSlots, label: 'Turnos del mes' },
            ]} />
          )}

          {entitySelected ? (
            <section className="nv-section" aria-label="Turnos">
              <div className="agenda-toolbar">
                <span className="nv-muted">
                  {appointmentsCount} {appointmentsCount === 1 ? 'turno' : 'turnos'}
                  {waitingCount > 0 && ` · ${waitingCount} en espera`}
                </span>
                {activeTab !== EAgendaTab.LIST && (
                  <span className="p-input-icon-left agenda-toolbar__search">
                    <i className="pi pi-search" aria-hidden="true" />
                    <InputText
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="Buscar paciente / documento"
                      aria-label="Buscar paciente o documento"
                      className="nv-w-m"
                    />
                  </span>
                )}
                <div className="nv-row-8 agenda-toolbar__tabs">
                  {/* Descargar es un control de la vista (DS): va en la barra, no dentro de la tabla */}
                  {lens === EAgendaLens.DAY && activeTab === EAgendaTab.LIST && (
                    <>
                      <Button
                        type="button"
                        rounded
                        className="nv-control nv-tip"
                        icon="pi pi-download"
                        aria-label="Descargar listado"
                        aria-haspopup
                        aria-controls="agenda-download-menu"
                        data-pr-tooltip="Descargar listado"
                        onClick={(e) => downloadMenuRef.current?.toggle(e)}
                      />
                      <Menu
                        model={downloadMenuItems}
                        popup
                        ref={downloadMenuRef}
                        id="agenda-download-menu"
                        className="nv-scope"
                      />
                    </>
                  )}
                  <SelectButton
                    value={activeTab}
                    options={tabOptions}
                    onChange={(e) => {
                      if (e.value) setActiveTab(e.value);
                    }}
                  />
                </div>
              </div>

              <div className="agenda-content">
                {/* Day views */}
                {lens === EAgendaLens.DAY && activeTab === EAgendaTab.LIST && (
                  <ListView
                    appointments={filteredAppointments}
                    loading={loading}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                    stateFilter={stateFilter}
                    onStateFilterChange={setStateFilter}
                    coverageFilter={coverageFilter}
                    onCoverageFilterChange={setCoverageFilter}
                    onClearFilters={() => {
                      setSearchText('');
                      setStateFilter(DEFAULT_STATE_FILTERS);
                      setCoverageFilter(null);
                    }}
                    professionalFilter={professionalFilter}
                    onProfessionalFilterChange={setProfessionalFilter}
                    onRegisterArrival={handleRegisterArrival}
                    onCallPatient={handleCallPatient}
                    onFinishAttention={handleFinishAttention}
                    onOpenDetail={handleOpenDetail}
                    viewMode={viewMode}
                    onLoadMore={loadMoreAppointments}
                    hasMore={hasMoreAppointments}
                    loadingMore={loadingMore}
                  />
                )}
                {lens === EAgendaLens.DAY && activeTab === EAgendaTab.SCHEDULE && (
                  <ScheduleView
                    appointments={filteredAppointments}
                    selectedAgendas={selectedAgendas}
                    buildingId={selectedBuildingId}
                    selectedDate={selectedDate}
                    availabilityRefreshKey={availabilityRefreshKey}
                    onSelectAvailabilitySlot={handleAvailabilitySlotSelect}
                    loading={loading}
                    onRegisterArrival={handleRegisterArrival}
                    onCallPatient={handleCallPatient}
                    onFinishAttention={handleFinishAttention}
                    onRevertStatus={handleRevertStatus}
                    onOpenDetail={handleOpenDetail}
                    selectedProfessionalId={selectedProfessional?.id ?? null}
                  />
                )}
                {/* Week views */}
                {lens === EAgendaLens.WEEK && activeTab === EAgendaTab.LIST && (
                  <WeekListView rows={weekListRows} loading={weekLoading} />
                )}
                {lens === EAgendaLens.WEEK && activeTab === EAgendaTab.SCHEDULE && (
                  <WeekScheduleView dayHeaders={weekDayHeaders} blocks={weekBlocks} />
                )}
                {/* Month views */}
                {lens === EAgendaLens.MONTH && activeTab === EAgendaTab.LIST && (
                  <MonthListView rows={monthListRows} loading={monthLoading} />
                )}
                {lens === EAgendaLens.MONTH && activeTab === EAgendaTab.SCHEDULE && (
                  <MonthCalendarView
                    cells={monthCells}
                    onDayClick={(date) => {
                      setSelectedDate(date);
                      setLens(EAgendaLens.DAY);
                    }}
                  />
                )}
              </div>
            </section>
          ) : (
            <p className="nv-muted agenda-content__empty">
              Elegí un profesional o una agenda para ver los turnos.
            </p>
          )}
        </div>
      ) : (
        // Pantallas completas (Nuevo turno, Sesiones, Fuera de agenda): fuera de .agenda-view, con su propia raíz nv-scope
        <>
          {activeScreen === 'newturno' && (
            <NewAppointmentModal
              onClose={handleNewAppointmentClose}
              onBooked={handleAppointmentBooked}
              onError={notify.error}
              initialDate={selectedDate}
              initialAgendas={availabilityBooking ? [availabilityBooking.agenda] : selectedAgendas}
              initialProfessionalId={selectedProfessional?.id ?? null}
              initialBuildingId={selectedBuildingId}
              initialSlotTime={availabilityBooking?.time ?? null}
              initialViewMode={viewMode}
              initialPatientId={reschedulePatientId}
              coverageLimited={coverageLimited}
              mode="single"
            />
          )}
          {activeScreen === 'newseries' && (
            <NewAppointmentModal
              onClose={() => setActiveScreen('agenda')}
              onBooked={handleAppointmentBooked}
              onError={notify.error}
              initialDate={selectedDate}
              initialBuildingId={selectedBuildingId}
              initialAgendas={selectedAgendas}
              initialProfessionalId={selectedProfessional?.id ?? null}
              initialViewMode={viewMode}
              coverageLimited={coverageLimited}
              mode="series"
            />
          )}
          {activeScreen === 'outofschedule' && (
            <OutOfScheduleScreen
              onClose={() => setActiveScreen('agenda')}
              onBooked={handleAppointmentBooked}
              onError={notify.error}
            />
          )}
        </>
      )}

      {/* Cancel / reschedule appointment modal */}
      {cancelTarget && (
        <CancelAppointmentModal
          appointmentId={cancelTarget.id}
          patientName={cancelTarget.patientName}
          visible={true}
          onHide={() => setCancelTarget(null)}
          onCancelled={loadData}
          mode={cancelTarget.mode}
          onReschedule={() => {
            setReschedulePatientId(cancelTarget.patientId ?? null);
            setCancelTarget(null);
            setActiveScreen('newturno');
          }}
        />
      )}

      {/* Patient check-in modal */}
      {checkInAppointmentId !== null && (
        <PatientCheckInModal
          appointmentId={checkInAppointmentId}
          visible={true}
          onHide={() => setCheckInAppointmentId(null)}
          onCompleted={loadData}
          onError={notify.error}
          onBillingRequested={(id, revertArrivalOnCancel) => {
            setCheckInAppointmentId(null);
            startOrderRegistration(id, revertArrivalOnCancel);
          }}
        />
      )}

      {/* Patient edit modal (from drawer) */}
      {editPatientAppointmentId !== null && (
        <PatientCheckInModal
          appointmentId={editPatientAppointmentId}
          visible={true}
          onHide={() => setEditPatientAppointmentId(null)}
          onCompleted={() => {
            setEditPatientAppointmentId(null);
            loadData();
          }}
          onError={notify.error}
          editMode
        />
      )}

      {/* Medical evolution modal */}
      <MedicalEvolutionModal
        visible={evolutionAppointment !== null}
        onHide={() => setEvolutionAppointment(null)}
        appointmentId={evolutionAppointment?.patientcarequeue ?? null}
        patientId={evolutionAppointment?.medereentity ?? null}
        servedTime={evolutionAppointment?.servedtime}
        patientName={evolutionAppointment?.patientname}
        onAttended={() => {
          setEvolutionAppointment(null);
          loadData();
        }}
        onMissed={() => {
          setEvolutionAppointment(null);
          loadData();
        }}
        onCancel={() => {
          setEvolutionAppointment(null);
          loadData();
        }}
        onBookAppointment={(patientIdToBook) => {
          setReschedulePatientId(patientIdToBook);
          setActiveScreen('newturno');
        }}
      />

      {/* Appointment detail drawer */}
      <AppointmentDetailDrawer
        appointment={liveDetailAppointment}
        visible={detailAppointment !== null}
        onHide={handleCloseDetail}
        onCancelAppointment={(id, name, patientId, profId) => {
          handleCancelAppointment(id, name, patientId, profId);
          handleCloseDetail();
        }}
        onRescheduleAppointment={(id, name, patientId, profId) => {
          handleRescheduleAppointment(id, name, patientId, profId);
          handleCloseDetail();
        }}
        onBilling={(id) => {
          startOrderRegistration(id);
          handleCloseDetail();
        }}
        onInvoicePendingOrder={(orderId, patientId, patientName) => {
          startBilling({ kind: 'invoice', orderId, patientId, patientName, subtitle: billingSubtitle(detailAppointment ?? undefined) });
          handleCloseDetail();
        }}
        onCollect={(patientId, patientName) => {
          startBilling({ kind: 'payment', patientId, patientName, subtitle: billingSubtitle(detailAppointment ?? undefined) });
          handleCloseDetail();
        }}
        onEditPatient={(id) => {
          setEditPatientAppointmentId(id);
          handleCloseDetail();
        }}
        onViewClinicalHistory={(patientId) => {
          setClinicalHistoryPatientId(patientId);
          handleCloseDetail();
        }}
        onViewLog={(id, patientName) => {
          setLogTarget({ id, patientName });
        }}
        onAssignPriority={(id, patientName, currentPriorityId) => {
          setPriorityTarget({ id, patientName, currentPriorityId });
        }}
        onRevertStatus={handleRevertStatus}
      />

      {/* Change location modal */}
      <ChangeLocationModal
        visible={showChangeLocation}
        onHide={() => setShowChangeLocation(false)}
        onConfirmed={loadData}
        onError={notify.error}
        initialProfessional={initialProfessional}
        initialSetupId={detailAppointment?.medicalschedulesetup ?? null}
      />

      {/* Appointment log modal */}
      <AppointmentLogModal
        visible={logTarget !== null}
        appointmentId={logTarget?.id ?? null}
        patientName={logTarget?.patientName}
        onHide={() => setLogTarget(null)}
      />

      {/* Assign priority modal */}
      <AssignPriorityModal
        visible={priorityTarget !== null}
        appointmentId={priorityTarget?.id ?? null}
        patientName={priorityTarget?.patientName}
        currentPriorityId={priorityTarget?.currentPriorityId ?? null}
        onHide={() => setPriorityTarget(null)}
        onAssigned={() => {
          setPriorityTarget(null);
          loadData();
        }}
        onError={notify.error}
      />

      {/* Clinical history modal */}
      <Dialog
        visible={clinicalHistoryPatientId !== null}
        onHide={() => setClinicalHistoryPatientId(null)}
        header="Historia clínica"
        modal
        closable
        draggable={false}
        maximizable
        style={{ width: '90vw', maxWidth: '1200px' }}
        contentStyle={{ padding: 0 }}
      >
        {clinicalHistoryPatientId !== null && (
          <PatientClinicalHistoryView patientId={clinicalHistoryPatientId} />
        )}
      </Dialog>

    </>
  );
}
